import { ReactNode } from "react";

import {
  Building2,
  CheckCircle2,
  Globe,
  Shield,
  ShoppingBag,
  User,
  UserCheck,
} from "lucide-react";

import { Badge } from "js/components/ui/badge";
import { Button } from "js/components/ui/button";
import { Separator } from "js/components/ui/separator";

import { PROPERTY_USE_OPTIONS } from "js/constants/property-use";

import { useGetOrganizationCredits } from "hooks/useOrganizations";

import { useAppSelector } from "../../../../redux/hooks";
import { formatCurrency } from "../../../../utils/utils";
import { StepComponentProps } from "../types";

interface StepComponentPropsWithNavigation extends StepComponentProps {
  onNavigateToStep?: (step: number) => void;
}

const TITLE_CASE_PARTICLES = new Set([
  "de",
  "del",
  "la",
  "las",
  "los",
  "y",
  "e",
  "o",
  "u",
  "en",
]);

const toTitleCase = (value: string | undefined | null) => {
  if (!value) return "";
  return value
    .toLowerCase()
    .split(/\s+/)
    .filter(Boolean)
    .map((word, index) =>
      index > 0 && TITLE_CASE_PARTICLES.has(word)
        ? word
        : word.charAt(0).toUpperCase() + word.slice(1),
    )
    .join(" ");
};

export const Step5ReviewAndOffers = ({
  formData,
  updateFormData,
  isAnimating,
  properties = [],
  onNavigateToStep,
}: StepComponentPropsWithNavigation) => {
  const { organizationData } = useAppSelector(
    (state) => state.entities.organization,
  );
  const webCheckIncluded =
    (organizationData as any)?.web_check_included ?? false;

  const { data: rawCreditData } = useGetOrganizationCredits();
  const tenantCreditKey =
    formData.tenantType === "moral" ? "tenant_pm" : "tenant_pf";
  const availableTenantCredits =
    (rawCreditData as any)?.organization?.quotes?.[tenantCreditKey] ?? 0;
  // While credits are still loading, optimistically assume quota exists to
  // avoid flashing the "link de pago" copy before data arrives.
  const hasTenantQuota = !rawCreditData || availableTenantCredits > 0;

  // Get property details
  const selectedProperty = properties.find((p) => p.id === formData.property);

  // Get property use label
  const propertyUseLabel =
    PROPERTY_USE_OPTIONS.find((option) => option.value === formData.propertyUse)
      ?.label || formData.propertyUse;

  const tenantInvestigationDescription =
    formData.tenantPaysApplication === null
      ? "Define quién cubrirá el costo antes de crear la solicitud."
      : formData.tenantPaysApplication
        ? "Costo cubierto por el inquilino"
        : hasTenantQuota
          ? "Cubierta por tu cuota"
          : "Se generará un link de pago para tu cuenta de asesor.";

  const orderedItems: {
    title: string;
    description?: string;
    extraContent?: ReactNode;
  }[] = [
    {
      title: "Investigación de inquilino",
      description: tenantInvestigationDescription,
    },
  ];

  const isBlindajeSelected = formData.alternativeJustice === true;
  const isPremiumSelected = formData.rentInsurance === true;

  const guarantorFullName = toTitleCase(
    [
      formData.guarantorName,
      formData.guarantorFirstSurname,
      formData.guarantorSecondSurname,
    ]
      .filter(Boolean)
      .join(" ")
      .trim(),
  );

  const guarantorTypeLabel =
    formData.guarantorType === "moral" ? "Persona Moral" : "Persona Física";

  const guarantorPhone = [
    formData.guarantorDialCode,
    formData.guarantorPhoneNumber,
  ]
    .filter(Boolean)
    .join(" ");

  if (isBlindajeSelected) {
    if (isPremiumSelected) {
      orderedItems.push({
        title: "Blindaje Legal Multiprotección Premium",
        extraContent: (
          <ul className="mt-2 space-y-1 text-xs text-gray-600 list-disc list-inside">
            <li>Incluye: Contrato, Convenio IJA y Póliza de Renta Protegida.</li>
            <li>
              Costo del servicio:{" "}
              <span className="font-semibold">{formatCurrency(7500)}</span>{" "}
              <span className="text-gray-500">
                (a cubrir el día de la firma).
              </span>
            </li>
          </ul>
        ),
      });
    } else {
      orderedItems.push({
        title: "Blindaje Legal Multiprotección",
        extraContent: (
          <ul className="mt-2 space-y-1 text-xs text-gray-600 list-disc list-inside">
            <li>Incluye: Contrato y Convenio de Justicia Alternativa.</li>
            <li>
              Costo del servicio:{" "}
              <span className="font-semibold">{formatCurrency(4600)}</span>{" "}
              <span className="text-gray-500">
                (a cubrir el día de la firma).
              </span>
            </li>
          </ul>
        ),
      });
    }
  }

  if (formData.rentInsurance && !isBlindajeSelected) {
    orderedItems.push({
      title: "Conviértelo en PREMIUM",
      description:
        "Cobertura total: gestionamos el proceso legal y los honorarios hasta recuperar el inmueble.",
    });
  }

  if (formData.requiresGuarantor && formData.creditInvestigation) {
    orderedItems.push({
      title: "Verificación integral del fiador",
      description: "Incluida en tu solicitud.",
    });
  }

  if (formData.webCheckRequired) {
    const webCheckPrice = formData.tenantType === "moral" ? 250 : 150;
    orderedItems.push({
      title:
        formData.tenantType === "moral"
          ? "Web Check Corporativo"
          : "Web Check Inquilino",
      description: webCheckIncluded
        ? "Verificación digital de antecedentes en medios y riesgos reputacionales. Costo: Incluido."
        : `Verificación digital de antecedentes en medios y riesgos reputacionales. Costo: ${formatCurrency(webCheckPrice)} MXN.`,
    });
  }

  const hasAllServices = isBlindajeSelected && isPremiumSelected;

  const showBlindajeUpsell = !isBlindajeSelected && !isPremiumSelected;
  const showVifUpsell =
    formData.requiresGuarantor && !formData.creditInvestigation;

  // Pricing breakdown (transparency)
  const investigationPrice = formData.tenantType === "moral" ? 1199 : 999;
  const investigationPersonLabel =
    formData.tenantType === "moral" ? "PM" : "PF";
  const includesVif =
    formData.requiresGuarantor && formData.creditInvestigation;
  const vifPrice = formData.guarantorType === "moral" ? 499 : 299;
  const vifPersonLabel = formData.guarantorType === "moral" ? "PM" : "PF";
  const includesWebCheck = formData.webCheckRequired === true;
  const webCheckPrice = formData.tenantType === "moral" ? 250 : 150;
  const webCheckCostToday =
    includesWebCheck && !webCheckIncluded ? webCheckPrice : 0;
  const vifCostToday = includesVif ? vifPrice : 0;
  const asesorPaysWithoutQuota =
    formData.tenantPaysApplication === false && !hasTenantQuota;
  const investigationCostToday =
    formData.tenantPaysApplication === true || asesorPaysWithoutQuota
      ? investigationPrice
      : 0;
  const totalToday =
    investigationCostToday + vifCostToday + webCheckCostToday;
  const signingTotal = isPremiumSelected ? 7500 : isBlindajeSelected ? 4600 : 0;

  const handleAddBlindaje = () => {
    updateFormData("alternativeJustice", true);
    if (formData.rentInsurance === null) {
      updateFormData("rentInsurance", false);
    }
  };

  return (
    <div
      className={`space-y-6 transition-all duration-500 ${isAnimating ? "opacity-0 translate-x-4" : "opacity-100 translate-x-0"}`}
    >
      {/* Header */}
      <div className="space-y-2">
        <div className="flex items-center gap-3 pb-2">
          <CheckCircle2 className="w-5 h-5 text-green-600" />
          <h3 className="text-lg font-semibold text-gray-900">
            Resumen de tu Solicitud
          </h3>
        </div>
        <p className="text-sm text-gray-500">
          Revisa los detalles de tu solicitud de renta y descubre servicios
          adicionales que pueden ser de tu interés.
        </p>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Property Summary */}
        <div className="border border-gray-200 rounded-lg p-4 bg-white">
          <div className="flex items-center gap-2 mb-4 pb-2 border-b border-gray-100">
            <Building2 className="w-5 h-5 text-blue-600" />
            <h4 className="text-base font-bold text-gray-900">
              Inmueble Seleccionado
            </h4>
          </div>
          <div className="space-y-3">
            <div>
              <p className="text-sm font-medium text-gray-900">
                {toTitleCase(
                  selectedProperty?.alias ||
                    selectedProperty?.label ||
                    "Inmueble",
                )}
              </p>
              <p className="text-xs text-gray-500 mt-1">
                {toTitleCase(
                  selectedProperty?.direccion || formData.address,
                )}
              </p>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-xs text-gray-600">Uso del inmueble:</span>
              <Badge variant="secondary" className="text-xs">
                {propertyUseLabel}
              </Badge>
            </div>
            <div className="space-y-2 pt-2 border-t border-gray-100">
              <div className="flex justify-between items-center">
                <span className="text-xs text-gray-600">Renta mensual:</span>
                <span className="text-sm font-semibold text-gray-900">
                  {formatCurrency(formData.rentAmount)}
                </span>
              </div>
              {formData.maintenanceAmount &&
                formData.maintenanceAmount !== "" && (
                  <div className="flex justify-between items-center">
                    <span className="text-xs text-gray-600">
                      Mantenimiento:
                    </span>
                    <span className="text-sm font-semibold text-gray-900">
                      {formatCurrency(formData.maintenanceAmount)}
                    </span>
                  </div>
                )}
            </div>
          </div>
        </div>

        {/* Tenant Summary */}
        <div className="border border-gray-200 rounded-lg p-4 bg-white">
          <div className="flex items-center gap-2 mb-4 pb-2 border-b border-gray-100">
            <User className="w-5 h-5 text-blue-600" />
            <h4 className="text-base font-bold text-gray-900">
              Inquilino Seleccionado
            </h4>
          </div>
          <div className="space-y-3">
            <div>
              <p className="text-sm font-medium text-gray-900">
                {toTitleCase(formData.tenantName) || "Inquilino"}
              </p>
              <Badge variant="outline" className="text-xs mt-1">
                Persona {formData.tenantType === "fisica" ? "Física" : "Moral"}
              </Badge>
            </div>
            <div className="space-y-2 pt-2 border-t border-gray-100">
              <div className="flex items-start gap-2">
                <span className="text-xs text-gray-600">Teléfono:</span>
                <span className="text-xs text-gray-900">
                  {formData.tenantPhone || "No especificado"}
                </span>
              </div>
              <div className="flex items-start gap-2">
                <span className="text-xs text-gray-600">Email:</span>
                <span className="text-xs text-gray-900 break-all">
                  {formData.tenantEmail || "No especificado"}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Guarantor and Configuration Summary */}
      <div className="space-y-4">
        {/* Guarantor Configuration */}
        <div className="border border-gray-200 rounded-lg p-4 bg-white">
          <div className="flex items-center gap-2 mb-4 pb-2 border-b border-gray-100">
            <Shield className="w-5 h-5 text-blue-600" />
            <h4 className="text-base font-bold text-gray-900">
              Configuración de Garantías
            </h4>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="flex items-center justify-between">
              <span className="text-xs text-gray-600">Requiere fiador:</span>
              <Badge
                variant={formData.requiresGuarantor ? "default" : "outline"}
                className="text-xs"
              >
                {formData.requiresGuarantor ? "Sí" : "No"}
              </Badge>
            </div>
            {formData.requiresGuarantor && (
              <>
                <div className="flex items-center justify-between">
                  <span className="text-xs text-gray-600">
                    Verificación integral del fiador:
                  </span>
                  <Badge
                    variant={
                      formData.creditInvestigation ? "default" : "outline"
                    }
                    className="text-xs"
                  >
                    {formData.creditInvestigation ? "Sí" : "No"}
                  </Badge>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-xs text-gray-600">
                    Inmueble como garantía:
                  </span>
                  <Badge
                    variant={
                      formData.propertyAsGuarantee ? "default" : "outline"
                    }
                    className="text-xs"
                  >
                    {formData.propertyAsGuarantee ? "Sí" : "No"}
                  </Badge>
                </div>
                {formData.creditInvestigation && formData.guarantorName && (
                  <div className="md:col-span-2">
                    <div className="border border-gray-200 rounded-lg bg-gray-50 p-4 space-y-4">
                      <div className="flex flex-wrap items-center justify-between gap-3">
                        <div className="flex items-center gap-3">
                          <div>
                            <p className="text-xs font-medium uppercase tracking-wide text-gray-500">
                              Datos del Fiador
                            </p>
                            <p className="text-sm font-semibold text-gray-900 leading-tight">
                              {guarantorFullName}
                            </p>
                          </div>
                        </div>
                      </div>
                      <dl className="grid gap-y-3 gap-x-6 text-xs text-gray-600 sm:grid-cols-2">
                        <div className="space-y-1">
                          <dt className="font-medium text-gray-500">
                            Tipo de persona
                          </dt>
                          <dd className="text-gray-900">
                            {guarantorTypeLabel}
                          </dd>
                        </div>
                        <div className="space-y-1">
                          <dt className="font-medium text-gray-500">
                            Correo electrónico
                          </dt>
                          <dd className="text-gray-900 break-all">
                            {formData.guarantorEmail}
                          </dd>
                        </div>
                        <div className="space-y-1">
                          <dt className="font-medium text-gray-500">
                            Teléfono
                          </dt>
                          <dd className="text-gray-900">{guarantorPhone}</dd>
                        </div>
                        {formData.guarantorBirthdate && (
                          <div className="space-y-1">
                            <dt className="font-medium text-gray-500">
                              Fecha de nacimiento
                            </dt>
                            <dd className="text-gray-900">
                              {formData.guarantorBirthdate}
                            </dd>
                          </div>
                        )}
                      </dl>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>

      <Separator className="my-6" />
      <div className="space-y-4">
        <div className="flex items-center gap-2 pb-2">
          <ShoppingBag className="w-5 h-5 text-blue-600" />
          <h3 className="text-base font-semibold text-gray-900">
            Resumen de tu pedido
          </h3>
        </div>

        <div className="border border-gray-200 rounded-lg bg-white overflow-hidden">
          <ul className="divide-y divide-gray-100">
            {orderedItems.map((item) => (
              <li key={item.title} className="flex items-start gap-3 px-5 py-4">
                <div className="flex-1">
                  <p className="text-sm font-semibold text-gray-900">
                    {item.title}
                  </p>
                  {item.description && item.description.trim() !== "" && (
                    <p className="text-xs text-gray-600 mt-1">
                      {item.description}
                    </p>
                  )}
                  {item.extraContent}
                </div>
              </li>
            ))}
          </ul>
        </div>

        <div className="border border-gray-200 rounded-lg p-5 bg-white space-y-3">
          <h4 className="text-base font-bold text-gray-900 pb-2 border-b border-gray-100">
            Detalle del costo
          </h4>
          <div className="space-y-2">
            <div className="flex items-center justify-between gap-3 text-xs">
              <span className="text-gray-600">
                Investigación de inquilino ({investigationPersonLabel}):
              </span>
              {formData.tenantPaysApplication === true ? (
                <span className="text-gray-900 font-medium">
                  {formatCurrency(investigationPrice)} MXN
                </span>
              ) : formData.tenantPaysApplication === false ? (
                hasTenantQuota ? (
                  <span className="text-gray-500 italic text-right">
                    Cubierta por tu cuota
                  </span>
                ) : (
                  <span className="text-gray-900 font-medium text-right">
                    {formatCurrency(investigationPrice)} MXN
                  </span>
                )
              ) : (
                <span className="text-gray-400 italic text-right">
                  Pendiente de definir
                </span>
              )}
            </div>
            {asesorPaysWithoutQuota && (
              <p className="text-[11px] text-gray-500 italic pl-1">
                Sin cuota disponible — se generará un link de pago para tu
                cuenta de asesor.
              </p>
            )}
            {includesVif && (
              <div className="flex items-center justify-between gap-3 text-xs">
                <span className="text-gray-600">
                  Verificación integral del fiador ({vifPersonLabel}):
                </span>
                <span className="text-gray-900 font-medium">
                  {formatCurrency(vifPrice)} MXN
                </span>
              </div>
            )}
            {includesWebCheck && (
              <div className="flex items-center justify-between gap-3 text-xs">
                <span className="text-gray-600">
                  Web Check{" "}
                  {formData.tenantType === "moral" ? "Corporativo" : "Inquilino"}
                  :
                </span>
                <span className="text-gray-900 font-medium">
                  {webCheckIncluded
                    ? "Incluido"
                    : `${formatCurrency(webCheckPrice)} MXN`}
                </span>
              </div>
            )}
          </div>
          <Separator className="my-2" />
          <div className="flex items-center justify-between">
            <span className="text-sm font-semibold text-gray-900">
              Total a pagar hoy:
            </span>
            <span className="text-base font-bold text-gray-900">
              {formatCurrency(totalToday)} MXN
            </span>
          </div>
          {signingTotal > 0 && (
            <>
              <Separator className="my-2" />
              <div className="flex items-center justify-between gap-3 text-xs">
                <span className="text-gray-600">
                  {isPremiumSelected
                    ? "Blindaje Legal Multiprotección Premium"
                    : "Blindaje Legal Multiprotección"}
                  :
                </span>
                <span className="text-gray-900 font-medium">
                  {formatCurrency(signingTotal)} MXN
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm font-semibold text-gray-900">
                  Total el día de la firma:
                </span>
                <span className="text-base font-bold text-gray-900">
                  {formatCurrency(signingTotal)} MXN
                </span>
              </div>
            </>
          )}
        </div>

        {showBlindajeUpsell && (
          <div className="border border-gray-200 rounded-lg p-5 bg-gray-50">
            <div className="flex items-start gap-3">
              <Shield className="w-5 h-5 text-green-600 mt-0.5" />
              <div className="flex-1 space-y-3">
                <div>
                  <p className="text-sm font-semibold text-gray-900">
                    Protege tu patrimonio. Es el momento de añadir el blindaje
                    legal.
                  </p>
                  <p className="text-xs text-gray-600 mt-1">
                    No esperes a aprobar para empezar el contrato. Avanza ambos
                    en paralelo y cierra sin fricción.
                  </p>
                </div>
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                  <Button
                    className="bg-[#0f5ef7] hover:bg-[#0d52e0]"
                    onClick={handleAddBlindaje}
                  >
                    Sí, añadir Blindaje Legal por $4,600 MXN
                  </Button>
                  <p className="text-xs font-bold text-gray-500">
                    (El costo se cubre el día de la firma del contrato)
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        {isBlindajeSelected && !isPremiumSelected && (
          <div className="border border-amber-200 rounded-lg p-5 bg-amber-50 space-y-3">
            <div>
              <p className="text-sm font-semibold text-amber-700">
                ⭐ ¡ÚLTIMA OPORTUNIDAD! Eleva tu protección a PREMIUM por solo
                $2,900 más.
              </p>
              <p className="text-xs text-amber-800 mt-2">
                Añade la Póliza de Renta Protegida y nosotros cubrimos el 100% de
                los honorarios de abogados hasta recuperar tu inmueble.
              </p>
            </div>
            <Button
              className="w-full sm:w-auto bg-[#0f5ef7] hover:bg-[#0d52e0]"
              onClick={() => updateFormData("rentInsurance", true)}
            >
              Sí, quiero la máxima tranquilidad
            </Button>
          </div>
        )}

        {showVifUpsell && (
          <div className="border border-gray-200 rounded-lg p-5 bg-gray-50">
            <div className="flex items-start gap-3">
              <UserCheck className="w-5 h-5 text-blue-600 mt-0.5" />
              <div className="flex-1 space-y-3">
                <div>
                  <p className="text-sm font-semibold text-gray-900">
                    Blinda tu garantía con la Verificación Integral del Fiador.
                  </p>
                  <p className="text-xs text-gray-600 mt-1">
                    Incluye antecedentes legales, Buró de Crédito, listas negras
                    y Web Check global del fiador.
                  </p>
                </div>
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                  <Button
                    className="bg-[#0f5ef7] hover:bg-[#0d52e0]"
                    onClick={() => onNavigateToStep?.(3)}
                  >
                    {`Sí, añadir Verificación por ${formatCurrency(vifPrice)} MXN`}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}

        {!formData.webCheckRequired && (
          <div className="border border-gray-200 rounded-lg p-5 bg-gray-50">
            <div className="flex items-start gap-3">
              <Globe className="w-5 h-5 text-blue-600 mt-0.5" />
              <div className="flex-1 space-y-3">
                <div>
                  <p className="text-sm font-semibold text-gray-900">
                    {formData.tenantType === "moral"
                      ? "Añade el Web Check Corporativo."
                      : "Añade el Web Check para tu inquilino."}
                  </p>
                  <p className="text-xs text-gray-600 mt-1">
                    Obtén un reporte con todos los links de noticias a nivel
                    global que mencionen a tu inquilino.
                  </p>
                </div>
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                  <Button
                    className="bg-[#0f5ef7] hover:bg-[#0d52e0]"
                    onClick={() => updateFormData("webCheckRequired", true)}
                  >
                    {webCheckIncluded
                      ? "Sí, agregar Web Check (Incluido)"
                      : `Sí, agregar Web Check por ${formatCurrency(formData.tenantType === "moral" ? 250 : 150)} MXN`}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}

        {hasAllServices && (
          <div className="border border-green-200 rounded-lg p-4 bg-green-50 text-sm text-green-800 flex items-center gap-2">
            <span className="text-lg">✅</span>
            <span>
              ¡Excelente decisión! Has elegido la máxima protección y
              tranquilidad para tu arrendamiento.
            </span>
          </div>
        )}
      </div>
    </div>
  );
};
